import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

export interface Track {
    id: string;
    title: string;
    artist: string;
    cover_image_url?: string;
    file_url: string;
    duration?: number;
    listing_id?: string;
}

interface GlobalMusicContextType {
    currentTrack: Track | null;
    playlist: Track[];
    queue: Track[];
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
    isShuffled: boolean;
    isRepeating: boolean;
    isVisible: boolean;
    play: (track: Track, newPlaylist?: Track[]) => void;
    pause: () => void;
    togglePlayPause: () => void;
    next: () => void;
    previous: () => void;
    setVolume: (volume: number) => void;
    setIsMuted: (muted: boolean) => void;
    setCurrentTime: (time: number) => void;
    toggleShuffle: () => void;
    toggleRepeat: () => void;
    addToQueue: (track: Track) => void;
    removeFromQueue: (trackId: string) => void;
    setIsVisible: (visible: boolean) => void;
}

const GlobalMusicContext = createContext<GlobalMusicContextType | undefined>(undefined);

export const GlobalMusicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
    const [playlist, setPlaylist] = useState<Track[]>([]);
    const [queue, setQueue] = useState<Track[]>([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [isMuted, setIsMuted] = useState(false);
    const [isShuffled, setIsShuffled] = useState(false);
    const [isRepeating, setIsRepeating] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Initialize audio element
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.volume = volume;
        }

        const audio = audioRef.current;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => {
            if (isRepeating) {
                audio.currentTime = 0;
                audio.play();
            } else {
                next();
            }
        };
        const handleError = () => {
            toast.error("Audio playback error");
            setIsPlaying(false);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [isRepeating]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume;
        }
    }, [volume, isMuted]);

    const play = useCallback(async (track: Track, newPlaylist?: Track[]) => {
        if (newPlaylist) {
            setPlaylist(newPlaylist);
            const idx = newPlaylist.findIndex(t => t.id === track.id);
            setCurrentIndex(idx >= 0 ? idx : 0);
        } else if (playlist.length === 0) {
            setPlaylist([track]);
            setCurrentIndex(0);
        }

        setCurrentTrack(track);
        setIsVisible(true);

        if (audioRef.current) {
            audioRef.current.src = track.file_url;
            try {
                await audioRef.current.play();
                setIsPlaying(true);
            } catch (err) {
                console.error("Playback failed", err);
                setIsPlaying(false);
            }
        }
    }, [playlist]);

    const pause = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }, []);

    const togglePlayPause = useCallback(() => {
        if (isPlaying) pause();
        else if (currentTrack) audioRef.current?.play().then(() => setIsPlaying(true));
    }, [isPlaying, currentTrack, pause]);

    const next = useCallback(() => {
        if (queue.length > 0) {
            const [nextTrack, ...remainingQueue] = queue;
            setQueue(remainingQueue);
            play(nextTrack);
            return;
        }

        if (playlist.length === 0) return;

        let nextIndex = (currentIndex + 1) % playlist.length;
        if (isShuffled) {
            nextIndex = Math.floor(Math.random() * playlist.length);
        }

        setCurrentIndex(nextIndex);
        play(playlist[nextIndex]);
    }, [playlist, currentIndex, isShuffled, queue, play]);

    const previous = useCallback(() => {
        if (playlist.length === 0) return;
        let prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
        setCurrentIndex(prevIndex);
        play(playlist[prevIndex]);
    }, [playlist, currentIndex, play]);

    const addToQueue = (track: Track) => {
        setQueue(prev => [...prev, track]);
        toast.success(`"${track.title}" added to queue`);
    };

    const removeFromQueue = (trackId: string) => {
        setQueue(prev => prev.filter(t => t.id !== trackId));
    };

    const handleSetCurrentTime = (time: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    return (
        <GlobalMusicContext.Provider value={{
            currentTrack, playlist, queue, isPlaying, currentTime, duration,
            volume, isMuted, isShuffled, isRepeating, isVisible,
            play, pause, togglePlayPause, next, previous,
            setVolume, setIsMuted, setCurrentTime: handleSetCurrentTime,
            toggleShuffle: () => setIsShuffled(!isShuffled),
            toggleRepeat: () => setIsRepeating(!isRepeating),
            addToQueue, removeFromQueue, setIsVisible
        }}>
            {children}
        </GlobalMusicContext.Provider>
    );
};

export const useMusic = () => {
    const context = useContext(GlobalMusicContext);
    if (!context) throw new Error('useMusic must be used within GlobalMusicProvider');
    return context;
};
